import {
  CheckCircleOutlined,
  CloseCircleOutlined,
  ExclamationCircleOutlined,
  InfoCircleOutlined,
} from '@ant-design/icons';
import { notification } from 'antd';
import { NotificationPlacement } from 'antd/es/notification/interface';
import React, { useCallback } from 'react';
import NotificationsContext from './NotificationContext';

// TODO : I had 2 update 2 types to make it sync. Should derive from one
export type ToastOptions = {
  title: string;
  description: string;
  type: 'Light' | 'Danger' | 'Success' | 'Warning' | 'Info';
  duration?: number;
  onClick?: () => void;
};

type Props = {
  children: React.ReactNode;
};

/**
 * ONE TOAST PER DISTINCT MESSAGE, NOT ONE PER EVENT.
 *
 * A page load fires several requests at once and the axios interceptor fires once
 * per failure, so a single dead endpoint used to stack ten identical
 * "Connection Error" toasts, each needing its own dismissal, the earliest pushing
 * anything else off screen.
 *
 * Deduplication is by CONTENT, because the call sites do not know about each
 * other: the interceptor cannot tell that nine siblings are failing the same way.
 * Reusing the key makes antd UPDATE the toast already on screen rather than
 * adding another, and restarts its dismissal timer - so a burst reads as one
 * message that keeps happening.
 *
 * Repeats are NOT counted. A "(7)" tells the reader something they can do nothing
 * with: the message is the same, and the action it implies is the same, whether
 * it arrived once or seven times. Counting also cost real machinery - a map of
 * live keys mirroring antd's own `maxCount` trim, because a toast evicted by
 * maxCount never fires `onClose` and so leaked its counter, and the next
 * occurrence came back mislabelled "(2)". None of that is needed to show one
 * toast instead of ten.
 */
const repeatKey = (data: ToastOptions) => `${data.type}|${data.title}|${data.description}`;

/**
 * The backstop for messages that are NOT identical: five different endpoints
 * failing five different ways still must not fill the screen.
 */
const MAX_TOASTS = 3;

const NotificationProvider = ({ children }: Props) => {
  const [api, contextHolder] = notification.useNotification({ maxCount: MAX_TOASTS });

  const openNotification = useCallback(
    (placement: NotificationPlacement, data: ToastOptions) => {
      api.info({
        key: repeatKey(data),
        message: data.title,
        description: data.description,
        placement,
        duration: data.duration,
        icon: {
          Success: <CheckCircleOutlined className="text-green-500" />,
          Danger: <CloseCircleOutlined className="text-app-danger" />,
          // Info, Warning and Light all used to carry CloseCircleOutlined - the
          // same ✗ as Danger - so anything that was not an error still read as
          // one. Only the colour differed.
          Info: <InfoCircleOutlined className="text-blue-500" />,
          Warning: <ExclamationCircleOutlined className="text-yellow-500" />,
          Light: <InfoCircleOutlined />,
        }[data.type],
        onClick: data.onClick,
        // pre-line so a multi-line description (one line per item) keeps its breaks.
        style: {
          width: 300,
          whiteSpace: 'pre-line',
        },
      });
    },
    [api]
  );

  const closeNotification = useCallback(() => {
    api.destroy();
  }, [api]);

  return (
    <NotificationsContext.Provider
      value={{
        openNotification,
        closeNotification,
      }}
    >
      {contextHolder}
      {children}
    </NotificationsContext.Provider>
  );
};
export default NotificationProvider;
